import cx from 'classnames';
import type { FunctionComponent, ButtonHTMLAttributes } from 'preact';
import styles from './Button.module.css';
import inputStyles from './inputCommon.module.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button style variant:
   * - `default`: white background, black text, dark gray border
   * - `primary`: blue background, white text
   * - `link`: looks like a link (underline, no background or padding). Note that to set the font
   *   size or weight, you need to set them on a parent element or use higher specificty than one class.
   * - `iconOnly`: 32px width/height, light gray background on hover only
   * - `custom`: only get rid of the browser default styles
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'link' | 'iconOnly' | 'custom';
}

/** button variants that get the `.standard` class */
const standardButtonVariants: ButtonProps['variant'][] = ['default', 'primary', 'iconOnly'];

/**
 * Render a styled button.
 *
 * To customize colors, you can either use the CSS variables (see `Button.module.css`)
 * or increase specificity (like `button.customClass`).
 */
export const Button: FunctionComponent<ButtonProps> = (props) => {
  const { variant = 'default', children, class: propsClassName, ...other } = props;

  const className = cx(
    styles.root,
    inputStyles.root,
    standardButtonVariants.includes(variant) && [styles.standard, inputStyles.standard],
    styles[variant],
    (variant === 'primary' || variant === 'default') && styles.defaultPrimary,
    propsClassName,
  );

  return (
    <button type="button" {...other} class={className}>
      {children}
    </button>
  );
};
